import { reactive, ref } from "@vue/composition-api";
import _axios from "@/axios";
import { handleFormErrors } from "./form-error";
import router from "@/router";
import { showSnackBar } from "@/composables/snackbar";
import { cloneDeep } from "lodash";

const currentClientPortal = ref(null);
const criteriaClientPortal = ref({
  group: 0,
  description: "",
  passmanId: "",
  reference: "",
  versions: [],
  isActive: [true],
  isInheritOnly: null,
});

export function useClientPortal() {
  let data = reactive({
    options: {},
    firstLoad: true,
    isLoading: false,
    loadingNotes: false,
    loadingSmac: false,
    clientPortalForm: {
      portalProfile: null,
      passmanId: null,
    },
    clientPortals: {},
    portalConfigurationFom: null,
    isInherit: false,
    portalPresentationModelDialog: false,
    portalPresentationThemeDialog: false,
    portalPresentationOtherDialog: false,
    portalConfigurationLanguageDialog: false,
    portalConfigurationPresentationDialog: false,
    portalConfigurationOtherDialog: false,
    portalConfigurationWelcomeDialog: false,
    portalConfigurationMeteoDialog: false,
    createClientPortalDialog: false,
    editInstallationNotesDialog: false,
    clientPortalFormRef: null,
    rules: {
      descriptionRules: [(v) => !!v || "La description est obligatoire"],
      languagesRules: [
        (v) => {
          if (v instanceof Array && v.length == 0) {
            return "Il faut sélectionner une langue.";
          }
          return !!v || "Il faut sélectionner une langue.";
        },
      ],
      default_languageRules: [(v) => !!v || "Il faut une langue par défault"],
      partnerRules: [(v) => !!v || "Il faut selectionner un partenaire"],
      passmanIdRules: [(v) => !!v || "Il faut saisir un PASSMAN ID"],
    },
    smac: {},
  });

  const closeAllDialogs = () => {
    data.portalPresentationModelDialog = false;
    data.portalPresentationThemeDialog = false;
    data.portalPresentationOtherDialog = false;
    data.portalConfigurationLanguageDialog = false;
    data.portalConfigurationPresentationDialog = false;
    data.portalConfigurationOtherDialog = false;
  };

  function createClientPortal() {
    data.isLoading = true;
    if (data.clientPortalFormRef != null) {
      const isValid = data.clientPortalFormRef.validate();
      if (!isValid) {
        data.isLoading = false;
        return;
      }
    }

    return _axios
      .post("/create-client-portal", data.clientPortalForm)
      .then((response) => {
        data.createClientPortalDialog = false;
        data.isLoading = true;
        router.push({
          name: "portail-details",
          params: { id: response.data.data.id },
        });
        showSnackBar();
      })
      .catch((error) => {
        const formErrors = error.response.data.violations;
        handleFormErrors(formErrors, data.rules, data.clientPortalFormRef);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  function listClientPortals(filterParams = null, searchTerm = null) {
    if (filterParams == null && searchTerm == null) {
      filterParams = criteriaClientPortal.value;
    }

    data.isLoading = true;

    const params = new URLSearchParams();

    params.set("page", data.options.page ?? 1);
    params.set("itemsPerPage", data.options.itemsPerPage ?? 100);

    if (data.options.sortBy && data.options.sortBy.length > 0) {
      if (data.options.sortDesc[0]) {
        params.set("order[desc]", data.options.sortBy[0]);
      } else {
        params.set("order[asc]", data.options.sortBy[0]);
      }
    }

    // use for global search (in header)
    if (searchTerm && searchTerm !== null) {
      params.set("searchTerm", searchTerm);
    }

    // use for list client in group page

    if (filterParams !== null) {
      if (
        filterParams.profile !== "" &&
        filterParams.profile > 0 &&
        filterParams.profile !== undefined
      ) {
        params.set("profile", filterParams.profile);
      }
      if (
        filterParams.group !== "" &&
        filterParams.group > 0 &&
        filterParams.group !== undefined
      ) {
        params.set("group", filterParams.group);
      }
      if (
        filterParams.description !== "" &&
        filterParams.description !== undefined
      ) {
        params.set("description[partial]", filterParams.description);
      }
      if (
        filterParams.reference !== "" &&
        filterParams.reference !== undefined
      ) {
        params.set("reference[partial]", filterParams.reference);
      }
      if (
        filterParams.passmanId !== "" &&
        filterParams.passmanId !== undefined
      ) {
        params.set("passmanId[partial]", filterParams.passmanId);
      }

      if (
        filterParams.externalId !== "" &&
        filterParams.externalId !== undefined
      ) {
        params.set("id[partial]", filterParams.externalId);
      }

      if (typeof filterParams.isActive == "boolean") {
        if (
          filterParams.isActive === true &&
          filterParams.isActive !== undefined
        ) {
          params.set("isActive", filterParams.isActive);
        }
      } else if (typeof filterParams.isActive == "object") {
        if (filterParams.isActive.length == 1) {
          params.set("isActive", filterParams.isActive[0]);
        }
      }

      if (
        filterParams.portal !== "" &&
        filterParams.portal !== undefined &&
        filterParams.portal !== null
      ) {
        params.set("portal:id", filterParams.portal);
      }
      if (filterParams.versions && filterParams.versions.length > 0) {
        params.set("portal[in]", filterParams.versions.join("|"));
      }
      if (filterParams.isInheritOnly) {
        params.set("isInheritOnly", filterParams.isInheritOnly);
      }
    }

    return _axios
      .get("/list-client-portal?" + params.toString())
      .then((response) => {
        data.clientPortals = response.data;
      })
      .catch(() => {
        const errorMessage =
          "Une erreur est survenue, si cela se reproduit contactez le support";
        alert(errorMessage);
      })
      .finally(() => {
        data.isLoading = false;
        data.firstLoad = false;
      });
  }

  function showClientPortal(id) {
    return _axios
      .get("/client-portal/" + id)
      .then((response) => {
        const clientPortal = response.data.data;

        currentClientPortal.value = clientPortal;
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function updateClientPortal(id) {
    data.isLoading = true;
    data.isLoading = true;
    if (data.clientPortalFormRef != null) {
      const isValid = data.clientPortalFormRef.validate();
      if (!isValid) {
        data.isLoading = false;
        data.isLoading = false;
        return;
      }
    }

    const {
      group,
      description,
      passmanId,
      isActive,
      useGroupCatalog,
      isSandboxModeActive,
      useCatalogGroup,
      hasMacLocking,
      proxyNumber,
      installationNotes,
      cardPrefix,
    } = data.clientPortalForm;
    const formData = {
      group,
      description,
      passmanId,
      isActive,
      useGroupCatalog,
      isSandboxModeActive,
      useCatalogGroup,
      hasMacLocking,
      proxyNumber,
      installationNotes,
      cardPrefix,
    };
    return _axios
      .put("/client-portal/" + id, formData)
      .then((response) => {
        const clientPortal = response.data.data;
        currentClientPortal.value = clientPortal;
        showSnackBar();
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      })
      .finally(() => {
        data.isLoading = false;
        data.isLoading = false;
      });
  }

  function updateInstallationNotes(id, notes) {
    data.loadingNotes = true;
    return _axios
      .put("/client-portal/" + id, {
        installationNotes: notes,
      })
      .then((response) => {
        const clientPortal = response.data.data;
        currentClientPortal.value = clientPortal;
        showSnackBar();
        data.editInstallationNotesDialog = false;
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      })
      .finally(() => {
        data.loadingNotes = false;
      });
  }

  function updatePortalConfiguration(
    id,
    portalConfigurationFom,
    isInherit = false,
    isBuiltIn = false,
    portalProfile = null,
    portalConfigurationFormRef = null
  ) {
    data.isLoading = true;
    if (portalConfigurationFormRef != null) {
      const isValid = portalConfigurationFormRef.validate();
      if (!isValid) {
        data.isLoading = false;
        return;
      }
    }

    // we ensure we have only the languages defined in the group
    if (isInherit) {
      const languages = [];
      portalConfigurationFom.languages.forEach(function (language) {
        if (
          currentClientPortal.value.group != null &&
          currentClientPortal.value.group.portalConfiguration != null &&
          currentClientPortal.value.group.portalConfiguration.languages.length >
            0 &&
          currentClientPortal.value.group.portalConfiguration.languages.includes(
            language
          )
        ) {
          languages.push(language);
        }
      });

      portalConfigurationFom.languages = languages;
    }

    const formData = {
      portal_configuration: cloneDeep(portalConfigurationFom),
      is_inherit: isInherit,
    };

    if (formData.portal_configuration.full_presentation.payment == null) {
      formData.portal_configuration.full_presentation = {};
    }
    if (formData.portal_configuration.basic_presentation.payment == null) {
      formData.portal_configuration.basic_presentation = {};
    }

    if (isBuiltIn && portalProfile != null) {
      formData["builtin_id"] = portalProfile.id;
    }

    return _axios
      .put(`/update-portal-configuration/${id}`, formData)
      .then((response) => {
        const clientPortal = response.data.data;
        currentClientPortal.value = clientPortal;
        showSnackBar();
        closeAllDialogs();
      })
      .catch((error) => {
        const formErrors = error.response.data.violations;
        handleFormErrors(formErrors, data.rules, portalConfigurationFormRef);
        alert(error.response.data.detail);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  function resetClientPortalCriteria() {
    criteriaClientPortal.value = {
      group: 0,
      description: "",
      passmanId: "",
      reference: "",
      versions: [],
      isActive: [true, false],
    };
  }

  // SMAC = Sessions, MAC, Attributes, Counts
  function fetchSmac(id) {
    data.loadingSmac = true;
    return _axios.get(`/client-portal-count/${id}`).then((response) => {
      data.smac = response.data.data;
      data.loadingSmac = false;
    });
  }

  function updatePortalConfigurationModel(id, formData) {
    if (formData.is_inherit || formData.builtin_id != null) {
      delete formData.model_id;
    }
    return _axios
      .put(`/update-client-portal-model/${id}`, formData)
      .then((response) => {
        const clientPortal = response.data.data;
        currentClientPortal.value = clientPortal;
        showSnackBar();
        closeAllDialogs();
      })
      .catch((error) => {
        alert(error.response.data.detail);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  function updatePortalConfigurationSparse(id, formData) {
    if (
      formData.full_presentation != null &&
      formData.full_presentation.payment == null
    ) {
      formData.full_presentation = {};
    }
    if (
      formData.basic_presentation != null &&
      formData.basic_presentation.payment == null
    ) {
      formData.basic_presentation = {};
    }

    return _axios
      .put(`/update-portal-configuration-sparse/${id}`, formData)
      .then((response) => {
        const clientPortal = response.data.data;
        currentClientPortal.value = clientPortal;
        showSnackBar();
        closeAllDialogs();
      })
      .catch((error) => {
        alert(error.response.data.detail);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  return {
    currentClientPortal,
    data,
    updateClientPortal,
    listClientPortals,
    showClientPortal,
    updatePortalConfiguration,
    createClientPortal,
    updateInstallationNotes,
    criteriaClientPortal,
    resetClientPortalCriteria,
    fetchSmac,
    updatePortalConfigurationModel,
    updatePortalConfigurationSparse,
  };
}
